import { ErrorHandler, Inject, Injectable, Optional, REQUEST } from '@angular/core';

import {
  BootstrapRequestContext,
  buildBuzzwavePublicUrl,
  BUZZWAVE_PUBLIC_PATHS,
  fetchWithBootstrapAuth,
} from './helpers/buzzwave-public-api';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  constructor(@Optional() @Inject(REQUEST) private readonly request?: Request) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleError = (error: any) => {
    const requestContext = this.resolveRequestContext();
    const errorPayload = {
      message: error.message || error.toString(),
      stack: error.stack || null,
      source: 'buzzwave-go',
      timestamp: new Date().toISOString(),
      campaignId: this.resolveCampaignId() ?? '',
      requestUrl: requestContext.requestUrl,
      requestPath: requestContext.requestPath,
      requestQueryString: requestContext.requestQueryString,
      requestPathname: requestContext.requestPathname,
    };

    const campaignId = errorPayload.campaignId;
    if (!campaignId) {
      console.warn('[GlobalErrorHandler] Skipping remote error log because no campaignId could be resolved');
      return;
    }
    const bootstrapContext = this.resolveBootstrapContext();

    void fetchWithBootstrapAuth(
      buildBuzzwavePublicUrl(BUZZWAVE_PUBLIC_PATHS.logError),
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(errorPayload),
      },
      campaignId,
      BUZZWAVE_PUBLIC_PATHS.logError,
      bootstrapContext,
    ).catch((err) => console.warn('Failed to send error log', err));
  };

  private resolveRequestContext(): {
    requestUrl: string;
    requestPath: string;
    requestQueryString: string;
    requestPathname: string;
  } {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      return {
        requestUrl: window.location.href,
        requestPath: url.pathname,
        requestQueryString: url.search,
        requestPathname: url.pathname,
      };
    }

    const requestUrl = this.request?.url ?? '';
    const url = new URL(requestUrl, 'http://localhost');
    return {
      requestUrl,
      requestPath: url.pathname,
      requestQueryString: url.search,
      requestPathname: url.pathname,
    };
  }

  private resolveBootstrapContext(): BootstrapRequestContext | undefined {
    if (typeof window !== 'undefined') {
      return {
        origin: window.location.origin,
        referer: window.location.href,
      };
    }

    const request = this.request;
    if (!request) {
      return undefined;
    }

    return {
      origin: request.headers.get('origin') ?? undefined,
      referer: request.headers.get('referer') ?? undefined,
    };
  }

  private resolveCampaignId(): string | null {
    if (typeof window === 'undefined') {
      const serverPath = this.request?.url ?? '';
      return this.resolveCampaignIdFromPath(serverPath);
    }

    const params = new URL(window.location.href).searchParams;
    const queryCandidates = [
      params.get('campaignId'),
      params.get('buzzwavecampaignid'),
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);

    if (queryCandidates.length > 0) {
      return queryCandidates[0];
    }

    const sessionMetadata = sessionStorage.getItem('buzzwave-campaign-metadata');
    if (sessionMetadata) {
      try {
        const parsed = JSON.parse(sessionMetadata);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].campaignId) {
          return parsed[0].campaignId;
        }
      } catch {
        // Ignore malformed session data and fall through to the path heuristic.
      }
    }

    return this.resolveCampaignIdFromPath(window.location.pathname);
  }

  private resolveCampaignIdFromPath(path: string): string | null {
    const pathSegments = new URL(path, 'http://localhost').pathname.split('/').filter(Boolean);
    const candidate = pathSegments.find((segment) => {
      const looksLikeGuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
        segment,
      );
      const looksLikeShortCampaignId = segment.length >= 5 && segment.length <= 7;
      return looksLikeGuid || looksLikeShortCampaignId;
    });
    return candidate ?? null;
  }
}
